import { useCallback, useRef, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../api";
import { ApiError } from "../api/client";
import type { DashboardField, EcPattern } from "../types/contract";
import { FieldSourcesCell } from "../features/field-sources/FieldSourcesCell";
import { HaneyOptions } from "../features/haney/HaneyOptions";
import { JdMachineLayers } from "./JdMachineLayers";
import { SoilSamplesOptions } from "./SoilSamplesOptions";
import { UploadedMachineDataNote } from "../features/add-data/UploadedMachineDataNote";

type DataSub = "samples" | "haney" | "ec" | "jd" | "sources";

export function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : "Something went wrong.";
}

type RunState = "queued" | "running" | "done" | "error";
interface FieldRun {
  id: string;
  name: string;
  state: RunState;
  msg?: string;
}

// Run an async op over the selected fields SEQUENTIALLY, reporting per-field progress (the backends
// were asked to handle several fields arriving one after another — sequential keeps the burst gentle).
export type BatchRunner = ReturnType<typeof useBatchRunner>;
// ONE runner lives in FieldActionsBar (not inside a batch panel) so it SURVIVES the panel being
// toggled closed/reopened mid-run — otherwise re-opening remounts a fresh panel with Run re-enabled
// and the user can fire a DUPLICATE batch of (expensive) model runs.
export function useBatchRunner() {
  const [runs, setRuns] = useState<FieldRun[]>([]);
  const [running, setRunning] = useState(false);
  const runningRef = useRef(false);
  const run = async (
    fields: DashboardField[],
    op: (f: DashboardField) => Promise<void>,
  ) => {
    if (runningRef.current) return; // a batch is already in flight — never start a second one
    runningRef.current = true;
    setRunning(true);
    setRuns(
      fields.map((f) => ({
        id: f.field_id,
        name: f.field_name,
        state: "queued" as RunState,
      })),
    );
    for (const f of fields) {
      setRuns((rs) =>
        rs.map((r) => (r.id === f.field_id ? { ...r, state: "running" } : r)),
      );
      try {
        await op(f);
        setRuns((rs) =>
          rs.map((r) => (r.id === f.field_id ? { ...r, state: "done" } : r)),
        );
      } catch (e) {
        setRuns((rs) =>
          rs.map((r) =>
            r.id === f.field_id ? { ...r, state: "error", msg: errMsg(e) } : r,
          ),
        );
      }
    }
    runningRef.current = false;
    setRunning(false);
  };
  // clear stale progress when the user switches action/selection — the runner is shared across
  // panels, so without this a completed batch's rows linger under an unrelated panel. No-op mid-run.
  // Stable identity (only touches refs/setState) so it's safe as an effect dependency.
  const reset = useCallback(() => {
    if (!runningRef.current) setRuns([]);
  }, []);
  return { runs, running, run, reset };
}

function BatchProgress({ runs }: { runs: FieldRun[] }) {
  if (runs.length === 0) return null;
  return (
    <ul className="mt-2 max-h-40 space-y-0.5 overflow-auto text-[11px]">
      {runs.map((r) => (
        <li key={r.id} className="flex items-center gap-1.5">
          {r.state === "done" ? (
            <CheckCircle2 size={11} className="shrink-0 text-emerald-400" />
          ) : r.state === "error" ? (
            <XCircle size={11} className="shrink-0 text-red-400" />
          ) : r.state === "running" ? (
            <Loader2 size={11} className="shrink-0 animate-spin text-accent" />
          ) : (
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-line" />
          )}
          <span className="truncate text-fg">{r.name}</span>
          {r.state === "error" && (
            <span className="truncate text-red-400">— {r.msg}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

const panel = "rounded-md border border-line bg-canvas/40 p-2 text-xs";
const ghostBtn =
  "rounded border border-line px-2 py-1 text-xs text-fg hover:bg-hover disabled:opacity-50";

// Batch fertility recs: compute + save the base buildup-to-critical recs for each selected field
// from its modeled maps.
function BatchEcScan({
  fields,
  runner,
}: {
  fields: DashboardField[];
  runner: BatchRunner;
}) {
  const { runs, running, run } = runner;
  const order = (pattern: EcPattern) =>
    void run(fields, (f) =>
      api.orderEcScan(f.field_id, pattern).then(() => void 0),
    );
  return (
    <div className={panel}>
      <div className="mb-1 text-[11px] font-medium">
        Order an EC scan on {fields.length} fields
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          disabled={running}
          className={ghostBtn}
          onClick={() => order("single_line")}
        >
          Single-line (standard)
        </button>
        <button
          disabled={running}
          className={ghostBtn}
          onClick={() => order("adaptive")}
        >
          Adaptive frequency
        </button>
        <button
          disabled={running}
          className={ghostBtn}
          onClick={() => order("dual_line")}
        >
          Dual-line
        </button>
      </div>
      <BatchProgress runs={runs} />
    </div>
  );
}

// Single-field "Add Field Data" — the full per-field data flows (samples/Haney/EC/JD), reused verbatim.
export function SingleFieldData({
  field,
  runner,
}: {
  field: DashboardField;
  runner: BatchRunner;
}) {
  const [sub, setSub] = useState<DataSub>("samples");
  const tab = (k: DataSub, label: string) => (
    <button
      className={`rounded px-2 py-1 text-[11px] ${sub === k ? "bg-accent text-canvas" : "text-fg hover:bg-hover"}`}
      onClick={() => setSub(k)}
    >
      {label}
    </button>
  );
  return (
    <div className={panel}>
      <div className="mb-2 flex flex-wrap items-center gap-1">
        {tab("sources", "Data sources")}
        {tab("samples", "Soil samples")}
        {tab("haney", "Haney test")}
        {tab("ec", "EC scan")}
        {tab("jd", "JD machine data")}
      </div>
      {sub === "sources" && (
        // active/inactive source toggles re-homed here from the old per-row cell: these still drive
        // the "forced manual" soil-props rule; expanding a source jumps to its tab.
        <FieldSourcesCell
          field={field}
          onExpand={(k) => {
            if (k === "samples" || k === "haney" || k === "ec" || k === "jd")
              setSub(k as DataSub);
          }}
        />
      )}
      {sub === "samples" && <SoilSamplesOptions field={field} />}
      {sub === "haney" && <HaneyOptions field={field} />}
      {sub === "ec" && <BatchEcScan fields={[field]} runner={runner} />}
      {sub === "jd" &&
        (field.has_jd_link ? (
          <JdMachineLayers field={field} />
        ) : field.has_yield ? (
          // yield from a bulk upload: the data IS here, so never say "link JD first".
          <UploadedMachineDataNote />
        ) : (
          <span className="text-[11px] text-muted">
            Link this field to JD Ops first (open its map to link).
          </span>
        ))}
    </div>
  );
}
